import gcoord from 'gcoord';

// 缓存键名
const CACHE_KEY = 'geolocation_cache';
// 缓存时间：10分钟
const CACHE_DURATION = 10 * 60 * 1000;

// 错误状态管理
let hasShownPermissionError = false;
let permissionCheckTimeout = null;

/**
 * 从localStorage获取缓存数据
 * @returns {Object|null} 缓存数据或null
 */
function getCacheFromStorage() {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            return JSON.parse(cached);
        }
    } catch (error) {
        console.warn('读取localStorage缓存失败:', error);
    }
    return null;
}

/**
 * 将数据保存到localStorage
 * @param {Object} data - 要缓存的数据
 */
function saveCacheToStorage(data) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
        console.warn('保存localStorage缓存失败:', error);
    }
}

/**
 * 清除localStorage缓存
 */
function clearCacheFromStorage() {
    try {
        localStorage.removeItem(CACHE_KEY);
    } catch (error) {
        console.warn('清除localStorage缓存失败:', error);
    }
}

/**
 * 智能错误处理
 * @param {Object} error - 地理位置错误对象
 * @param {Function} reject - Promise reject函数
 * @param {boolean} silent - 是否静默模式
 */
function handleGeolocationError(error, reject, silent = false) {
    let errorMessage = "获取位置失败";
    let shouldAlert = false;

    switch (error.code) {
        case 1: // PERMISSION_DENIED
            // 用户拒绝权限，只在第一次显示提示
            if (!hasShownPermissionError) {
                errorMessage = "需要位置权限才能获取您的位置，请在浏览器设置中允许位置权限";
                shouldAlert = true;
                hasShownPermissionError = true;

                // 5秒后重置错误状态，允许用户重新尝试
                if (permissionCheckTimeout) {
                    clearTimeout(permissionCheckTimeout);
                }
                permissionCheckTimeout = setTimeout(() => {
                    hasShownPermissionError = false;
                }, 5000);
            } else {
                errorMessage = "位置权限被拒绝，请手动开启位置权限后重试";
                shouldAlert = true; // 改为总是提示，让用户知道需要手动操作
            }
            break;

        case 2: // POSITION_UNAVAILABLE
            errorMessage = "位置信息不可用，请检查设备定位功能是否开启";
            shouldAlert = true;
            break;

        case 3: // TIMEOUT
            errorMessage = "获取位置超时，请检查网络连接和GPS信号";
            shouldAlert = true;
            break;

        default:
            errorMessage = `获取位置失败: ${error.message}`;
            shouldAlert = true;
    }

    console.error('地理位置错误:', errorMessage, error);

    if (shouldAlert && !silent) {
        alert(errorMessage);
    }

    reject(error);
}

/**
 * 获取平台坐标数据
 * 自动处理缓存逻辑：如果缓存未过期则使用缓存，否则重新获取
 * @param {Object} options - 配置选项
 * @param {boolean} options.enableHighAccuracy - 是否启用高精度模式，默认true
 * @param {number} options.timeout - 超时时间（毫秒），默认5000
 * @param {number} options.maximumAge - 最大缓存时间（毫秒），默认0
 * @param {boolean} options.silent - 是否静默模式，不显示错误弹窗，默认false
 * @param {number} options.extraTimeout - 额外超时保护时间（毫秒），默认10000
 * @returns {Promise<Object>} 返回坐标数据对象
 */
export function getPosition(options = {}) {
    const {
        enableHighAccuracy = true,
        timeout = 5000,
        maximumAge = 0,
        silent = false,
        extraTimeout = 10000
    } = options;

    return new Promise((resolve, reject) => {
        // 检查localStorage缓存是否有效
        const cachedData = getCacheFromStorage();
        if (cachedData && cachedData.timestamp) {
            const now = Date.now();
            if (now - cachedData.timestamp < CACHE_DURATION) {
                console.log('使用localStorage缓存的坐标数据');
                resolve(cachedData);
                return;
            } else {
                console.log('localStorage缓存已过期，重新获取坐标');
                clearCacheFromStorage();
            }
        }

        // 检查浏览器是否支持地理位置API
        if (!("geolocation" in navigator)) {
            const error = new Error("浏览器不支持Geolocation API");
            console.error(error.message);
            if (!silent) {
                alert(error.message);
            }
            reject(error);
            return;
        }

        // 添加额外的超时保护机制
        let isResolved = false;
        const extraTimeoutId = setTimeout(() => {
            if (!isResolved) {
                isResolved = true;
                const error = new Error("获取位置超时");
                console.error(error.message);
                if (!silent) {
                    alert("获取位置超时，请检查网络连接和定位权限");
                }
                reject(error);
            }
        }, extraTimeout);

        // 获取地理位置
        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (isResolved) return; // 防止重复处理
                isResolved = true;
                clearTimeout(extraTimeoutId);

                const { latitude, longitude } = position.coords;
                console.log("原始坐标 - 纬度:", latitude, "经度:", longitude);

                // 坐标转换：WGS84 -> GCJ02
                const gcj02Point = gcoord.transform(
                    [longitude, latitude],  // 原始坐标
                    gcoord.WGS84,          // 输入坐标系
                    gcoord.GCJ02           // 输出坐标系
                );

                const result = {
                    original: {
                        latitude,
                        longitude
                    },
                    gcj02: {
                        longitude: gcj02Point[0],
                        latitude: gcj02Point[1]
                    },
                    timestamp: Date.now()
                };

                // 保存到localStorage缓存
                saveCacheToStorage(result);
                console.log('坐标数据已保存到localStorage缓存');

                console.log('坐标转换结果:', result);
                resolve(result);
            },
            (error) => {
                if (isResolved) return; // 防止重复处理
                isResolved = true;
                clearTimeout(extraTimeoutId);

                if (silent) {
                    // 静默模式，只记录错误，不弹窗
                    console.error('获取位置失败:', error.message);
                    reject(error);
                } else {
                    // 正常模式，使用智能错误处理
                    handleGeolocationError(error, reject, silent);
                }
            },
            {
                enableHighAccuracy,
                timeout,
                maximumAge
            }
        );
    });
}

/**
 * 清除localStorage坐标缓存
 */
export function clearLocationCache() {
    clearCacheFromStorage();
    console.log('localStorage坐标缓存已清除');
}

/**
 * 获取localStorage缓存状态
 * @returns {Object} 缓存状态信息
 */
export function getCacheStatus() {
    const cachedData = getCacheFromStorage();

    if (!cachedData || !cachedData.timestamp) {
        return {
            hasCache: false,
            remainingTime: 0,
            storageType: 'localStorage'
        };
    }

    const now = Date.now();
    const elapsed = now - cachedData.timestamp;
    const remaining = Math.max(0, CACHE_DURATION - elapsed);

    return {
        hasCache: true,
        remainingTime: remaining,
        isExpired: remaining <= 0,
        storageType: 'localStorage',
        cachedData: cachedData
    };
}

/**
 * 重置错误状态（用于测试或手动重置）
 */
export function resetErrorState() {
    hasShownPermissionError = false;
    if (permissionCheckTimeout) {
        clearTimeout(permissionCheckTimeout);
        permissionCheckTimeout = null;
    }
    console.log('错误状态已重置');
}

/**
 * 诊断地理位置API状态
 * @returns {Object} 诊断结果
 */
export function diagnoseGeolocation() {
    const result = {
        supported: false,
        permission: 'unknown',
        cacheStatus: null,
        errorState: hasShownPermissionError,
        recommendations: []
    };

    // 检查浏览器支持
    if ("geolocation" in navigator) {
        result.supported = true;
        result.recommendations.push('浏览器支持地理位置API');
    } else {
        result.recommendations.push('浏览器不支持地理位置API，建议升级浏览器');
        return result;
    }

    // 检查缓存状态
    const cacheStatus = getCacheStatus();
    result.cacheStatus = cacheStatus;

    // 检查权限状态（异步）
    if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {
            result.permission = permissionStatus.state;
            console.log('地理位置权限状态:', permissionStatus.state);

            if (permissionStatus.state === 'denied') {
                result.recommendations.push('位置权限被拒绝，请在浏览器设置中允许位置权限');
            } else if (permissionStatus.state === 'prompt') {
                result.recommendations.push('位置权限未设置，首次使用时会请求权限');
            } else if (permissionStatus.state === 'granted') {
                result.recommendations.push('位置权限已授予');
            }
        }).catch(() => {
            result.recommendations.push('无法检测权限状态，可能需要用户手动检查');
        });
    } else {
        result.recommendations.push('无法检测权限状态，请手动检查浏览器设置');
    }

    // 检查网络连接
    if (navigator.onLine) {
        result.recommendations.push('网络连接正常');
    } else {
        result.recommendations.push('网络连接异常，可能影响定位功能');
    }

    // 检查HTTPS
    if (location.protocol === 'https:') {
        result.recommendations.push('使用HTTPS协议，地理位置API可用');
    } else {
        result.recommendations.push('建议使用HTTPS协议，某些浏览器在HTTP下会限制地理位置API');
    }

    console.log('地理位置诊断结果:', result);
    return result;
}
